/**
 * SuperAdmin Users — full CRUD + defaulter overrides.
 *
 * Reads / mutates only existing endpoints:
 *   GET    /superadmin/users?search=&role=&category=
 *   POST   /auth/register
 *   POST   /auth/reset-password  { user_id }
 *   PUT    /superadmin/users/{id}/block   (toggle)
 *   DELETE /superadmin/users/{id}?force=…
 *   DELETE /superadmin/users/students/bulk-delete?force=true
 *   GET    /superadmin/defaulter-overrides
 *   POST   /superadmin/defaulter-override { student_id, hours, reason }
 *   DELETE /superadmin/defaulter-override/{student_id}
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertOctagon, KeyRound, Search, Trash2, UserMinus, UserPlus, X, ShieldCheck } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const ROLE_OPTS = [
  { value: 'all',     label: 'All roles' },
  { value: 'student', label: 'Students' },
  { value: 'admin',   label: 'Faculty (admin)' },
  { value: 'clerk',   label: 'Record Clerk' },
];

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function UsersPage({ ctx }) {
  const { showToast } = ctx;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [overrideStudent, setOverrideStudent] = useState(null);
  const [overrides, setOverrides] = useState({ overrides: [], active_count: 0, count: 0 });
  const [showOverridesDrawer, setShowOverridesDrawer] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (q) qs.set('search', q);
      if (role !== 'all') qs.set('role', role);
      if (category !== 'all') qs.set('category', category);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const { data } = await apiClient.get(`${endpoints.users.superadminList}${suffix}`);
      setRows((Array.isArray(data) ? data : []).filter((u) => u.role !== 'superadmin'));
      setSelected(new Set());
    } catch (e) {
      showToast && showToast('Failed to load users', 'err');
    } finally { setLoading(false); }
  }, [q, role, category, showToast]);

  const loadOverrides = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.defaulters.overridesList);
      setOverrides(data || { overrides: [], active_count: 0, count: 0 });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    apiClient.get(endpoints.auth.studentCategories).then((r) => setCategories(r.data?.categories || [])).catch(() => {});
    loadOverrides();
  }, [loadOverrides]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const stats = useMemo(() => ({
    total: rows.length,
    students: rows.filter((u) => u.role === 'student').length,
    admins: rows.filter((u) => u.role === 'admin').length,
    blocked: rows.filter((u) => !u.is_active).length,
  }), [rows]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));

  const blockToggle = async (u) => {
    try {
      await apiClient.put(endpoints.users.superadminBlock(u.id));
      showToast && showToast(`${u.full_name} ${u.is_active ? 'blocked' : 'unblocked'}`, 'ok');
      await load();
    } catch (e) { showToast && showToast(e?.response?.data?.detail || 'Failed', 'err'); }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.full_name} to said@1234?`)) return;
    try {
      await apiClient.post(endpoints.auth.resetPassword, { user_id: u.id });
      showToast && showToast(`Password reset for ${u.full_name}`, 'ok');
    } catch (e) { showToast && showToast(e?.response?.data?.detail || 'Reset failed', 'err'); }
  };

  const deleteOne = async (u) => {
    const forceReq = !window.confirm(`Delete ${u.full_name}? (Cancel to attempt with force=true instead)`);
    try {
      await apiClient.delete(`${endpoints.users.superadminById(u.id)}${forceReq ? '?force=true' : ''}`);
      showToast && showToast(`${u.full_name} deleted`, 'ok');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Delete failed';
      if (!forceReq && window.confirm(`${msg}\n\nForce delete anyway?`)) {
        try {
          await apiClient.delete(`${endpoints.users.superadminById(u.id)}?force=true`);
          showToast && showToast(`${u.full_name} force-deleted`, 'ok');
          await load();
        } catch (e2) { showToast && showToast(e2?.response?.data?.detail || 'Delete failed', 'err'); }
      } else { showToast && showToast(msg, 'err'); }
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Force-delete ${selected.size} selected user(s)? Cascades pending orders/returns.`)) return;
    let ok = 0, fail = 0;
    for (const id of selected) {
      try { await apiClient.delete(`${endpoints.users.superadminById(id)}?force=true`); ok++; }
      catch { fail++; }
    }
    showToast && showToast(`Deleted ${ok}, failed ${fail}`, ok > 0 ? 'ok' : 'err');
    await load();
  };

  const grantOverride = async ({ student_id, hours, reason }) => {
    try {
      await apiClient.post(endpoints.defaulters.overrideCreate, { student_id, hours, reason });
      showToast && showToast('Override granted', 'ok');
      setOverrideStudent(null);
      loadOverrides();
    } catch (e) { showToast && showToast(e?.response?.data?.detail || 'Failed', 'err'); }
  };

  const revokeOverride = async (student_id) => {
    if (!window.confirm('Revoke this override now?')) return;
    try {
      await apiClient.delete(endpoints.defaulters.overrideDelete(student_id));
      showToast && showToast('Override revoked', 'ok');
      loadOverrides();
    } catch (e) { showToast && showToast(e?.response?.data?.detail || 'Failed', 'err'); }
  };

  return (
    <div data-testid="spa-users-page">
      <div className="ap-eyebrow">SuperAdmin Console</div>
      <h1 className="ap-page-title">Users</h1>
      <p className="ap-page-lede">
        Full user management: create, block/unblock, delete, and grant defaulter overrides.
      </p>

      <div className="ap-ind-row" role="group" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="ap-ind"><div className="ap-ind-label">Visible</div><div className="ap-ind-value">{stats.total}</div><div className="ap-ind-hint">Filtered by search</div></div>
        <div className="ap-ind"><div className="ap-ind-label">Students</div><div className="ap-ind-value">{stats.students}</div><div className="ap-ind-hint">BDS / PG</div></div>
        <div className="ap-ind"><div className="ap-ind-label">Faculty</div><div className="ap-ind-value">{stats.admins}</div><div className="ap-ind-hint">admin logins</div></div>
        <div className="ap-ind"><div className="ap-ind-label"><em className="is-warn" />Blocked</div><div className="ap-ind-value">{stats.blocked}</div><div className="ap-ind-hint">Inactive</div></div>
      </div>

      <div className="ap-mt-6" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 420 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 12, opacity: 0.5 }} />
          <input className="ap-input" style={{ paddingLeft: 30 }} placeholder="Search name, username, email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="spa-users-search" />
        </div>
        <select className="ap-input" style={{ maxWidth: 170 }} value={role} onChange={(e) => setRole(e.target.value)} data-testid="spa-users-role-filter">
          {ROLE_OPTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select className="ap-input" style={{ maxWidth: 180 }} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="spa-users-category-filter">
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="button" className="ap-btn ap-btn-primary" onClick={() => setAddOpen(true)} data-testid="spa-users-add-btn">
          <UserPlus size={14} style={{ marginRight: 6 }} /> Add user
        </button>
        <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setShowOverridesDrawer(true)} data-testid="spa-users-overrides-btn">
          <ShieldCheck size={14} style={{ marginRight: 6 }} /> Overrides ({overrides.active_count})
        </button>
      </div>

      {selected.size > 0 && (
        <div className="ap-mt-3" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#FEF3F2', border: '1px solid #FEC7C1', borderRadius: 8 }}>
          <strong>{selected.size} selected</strong>
          <button type="button" className="ap-btn ap-btn-ghost" onClick={bulkDelete} data-testid="spa-users-bulk-delete">
            <Trash2 size={14} style={{ marginRight: 4 }} /> Force delete
          </button>
        </div>
      )}

      <div className="ap-mt-6">
        {loading ? <div className="ap-empty">Loading users…</div>
          : rows.length === 0 ? <div className="ap-empty" data-testid="spa-users-empty">No users match your filters.</div>
          : (
          <div className="ap-table-wrap" data-testid="spa-users-table">
            <table className="ap-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} data-testid="spa-users-select-all" />
                  </th>
                  <th>Name</th>
                  <th>Username · Email</th>
                  <th>Role</th>
                  <th>Category / Roll</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} data-testid={`spa-users-row-${u.id}`}>
                    <td>
                      <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} data-testid={`spa-users-select-${u.id}`} />
                    </td>
                    <td style={{ fontWeight: 500 }}>{u.full_name || '—'}</td>
                    <td>
                      <div>@{u.username}</div>
                      <div className="ap-subtle">{u.email || '—'}</div>
                    </td>
                    <td><span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase' }}>{u.role}</span></td>
                    <td>
                      <div>{u.student_category || '—'}</div>
                      {u.roll_number && <div className="ap-subtle">Roll {u.roll_number}</div>}
                    </td>
                    <td>{u.is_active
                      ? <span style={{ color: '#0F766E', fontSize: 12 }}>● Active</span>
                      : <span style={{ color: '#B04A3C', fontSize: 12 }}>● Blocked</span>}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => resetPw(u)} title="Reset password" data-testid={`spa-users-reset-${u.id}`}><KeyRound size={13} /></button>{' '}
                      <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => blockToggle(u)} title={u.is_active ? 'Block' : 'Unblock'} data-testid={`spa-users-block-${u.id}`}><UserMinus size={13} /></button>{' '}
                      {u.role === 'student' && (
                        <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => setOverrideStudent(u)} title="Grant defaulter override" data-testid={`spa-users-override-${u.id}`}><AlertOctagon size={13} /></button>
                      )}{' '}
                      <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => deleteOne(u)} title="Delete" data-testid={`spa-users-delete-${u.id}`}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addOpen && <AddUserModal categories={categories} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); showToast && showToast('User created', 'ok'); }} onError={(m) => showToast && showToast(m, 'err')} />}
      {overrideStudent && <OverrideModal student={overrideStudent} onClose={() => setOverrideStudent(null)} onSubmit={grantOverride} />}
      {showOverridesDrawer && <OverridesDrawer overrides={overrides} onClose={() => setShowOverridesDrawer(false)} onRevoke={revokeOverride} />}
    </div>
  );
}

function AddUserModal({ categories, onClose, onCreated, onError }) {
  const [f, setF] = useState({ username: '', password: '', email: '', role: 'student', full_name: '', student_category: '', roll_number: '' });
  const [saving, setSaving] = useState(false);
  const ok = f.username && f.password && f.email && f.full_name && (f.role !== 'student' || f.student_category);
  const submit = async () => {
    setSaving(true);
    try { await apiClient.post(endpoints.auth.register, f); onCreated(); }
    catch (e) { onError(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div className="ap-modal-scrim" onClick={onClose} data-testid="spa-users-add-modal">
      <div className="ap-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="ap-modal-header">
          <div><h3 style={{ margin: 0, fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 20 }}>Add user</h3></div>
          <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="ap-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ gridColumn: '1 / -1' }}><div className="ap-field-label">Role</div>
            <select className="ap-input" value={f.role} onChange={(e) => set('role', e.target.value)} data-testid="spa-users-add-role">
              <option value="student">Student</option>
              <option value="admin">Faculty (admin)</option>
              <option value="clerk">Record Clerk</option>
            </select></label>
          <label><div className="ap-field-label">Full name</div><input className="ap-input" value={f.full_name} onChange={(e) => set('full_name', e.target.value)} data-testid="spa-users-add-fullname" /></label>
          <label><div className="ap-field-label">Username</div><input className="ap-input" value={f.username} onChange={(e) => set('username', e.target.value)} data-testid="spa-users-add-username" /></label>
          <label><div className="ap-field-label">Email</div><input className="ap-input" value={f.email} onChange={(e) => set('email', e.target.value)} data-testid="spa-users-add-email" /></label>
          <label><div className="ap-field-label">Password</div><input type="password" className="ap-input" value={f.password} onChange={(e) => set('password', e.target.value)} data-testid="spa-users-add-password" /></label>
          {f.role === 'student' && (<>
            <label><div className="ap-field-label">Category</div>
              <select className="ap-input" value={f.student_category} onChange={(e) => set('student_category', e.target.value)}>
                <option value="">— select —</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label><div className="ap-field-label">Roll number</div><input className="ap-input" value={f.roll_number} onChange={(e) => set('roll_number', e.target.value)} /></label>
          </>)}
        </div>
        <div className="ap-modal-footer">
          <button type="button" className="ap-btn ap-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="ap-btn ap-btn-primary" onClick={submit} disabled={!ok || saving} data-testid="spa-users-add-submit">{saving ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

function OverrideModal({ student, onClose, onSubmit }) {
  const [hours, setHours] = useState(48);
  const [reason, setReason] = useState('');
  return (
    <div className="ap-modal-scrim" onClick={onClose} data-testid="spa-users-override-modal">
      <div className="ap-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div className="ap-modal-header">
          <div>
            <h3 style={{ margin: 0, fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 20 }}>Grant defaulter override</h3>
            <p className="ap-subtle" style={{ margin: '4px 0 0', fontSize: 12 }}>{student.full_name}</p>
          </div>
          <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="ap-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label><div className="ap-field-label">Override window (hours, max 720)</div>
            <input className="ap-input" type="number" min={1} max={720} value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="spa-users-override-hours" /></label>
          <label><div className="ap-field-label">Reason (audited)</div>
            <textarea className="ap-textarea" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="spa-users-override-reason" /></label>
        </div>
        <div className="ap-modal-footer">
          <button type="button" className="ap-btn ap-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="ap-btn ap-btn-primary" onClick={() => onSubmit({ student_id: student.id, hours, reason })} disabled={!hours || hours < 1} data-testid="spa-users-override-submit">Grant</button>
        </div>
      </div>
    </div>
  );
}

function OverridesDrawer({ overrides, onClose, onRevoke }) {
  const list = overrides.overrides || [];
  return (
    <div className="ap-modal-scrim" onClick={onClose} data-testid="spa-users-overrides-drawer">
      <div className="ap-modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div className="ap-modal-header">
          <div>
            <h3 style={{ margin: 0, fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 20 }}>Defaulter overrides</h3>
            <p className="ap-subtle" style={{ margin: '4px 0 0', fontSize: 12 }}>{overrides.active_count} active · {overrides.count} total (last 30 days)</p>
          </div>
          <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="ap-modal-body">
          {list.length === 0 ? <div className="ap-empty">No overrides on record.</div> : (
            <div className="ap-table-wrap"><table className="ap-table">
              <thead><tr><th>Student</th><th>Status</th><th>Until</th><th>Granted by</th><th>Reason</th><th /></tr></thead>
              <tbody>
                {list.map((o) => (
                  <tr key={o.student_id}>
                    <td><div style={{ fontWeight: 500 }}>{o.student_name}</div><div className="ap-subtle">{o.student_category || '—'}</div></td>
                    <td>{o.is_active ? <span style={{ color: '#0F766E' }}>● Active</span> : <span className="ap-subtle">Expired</span>}</td>
                    <td>{fmt(o.until)}</td>
                    <td>{o.granted_by || '—'}</td>
                    <td className="ap-subtle" style={{ maxWidth: 220 }}>{o.reason || '—'}</td>
                    <td>{o.is_active && (
                      <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => onRevoke(o.student_id)}>Revoke</button>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      </div>
    </div>
  );
}
